import React, { useEffect, useState, FC } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client'
import Toolbar from '@mui/material/Toolbar';
import AddIcon from '@mui/icons-material/Add';
import MDButtonRoot from "./MDButton/MDButtonRoot";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import setThemeObj from './../hooks/useThemeMUI';
import StyledBox from "./StyleBox";
import MDBoxRoot from './MDBox/MDBoxRoot';

import EditPackageDialog from './EditPackageDialog';
import { useScreenSize } from "../hooks/ScreenSizeContext";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { DataGrid, GridColDef, GridToolbarContainer, GridActionsCellItem, GridRowId } from '@mui/x-data-grid';

import * as types from "../gql/graphql";
import { Dialog, DialogActions, DialogContent, styled } from '@mui/material';
import MDTypographyRoot from './MDTypography/MDTypographyRoot';
import { useQueryAll } from '../apolloClient';

const getPackagesQuery = gql(`
  query GetPackages($after: Cursor) {
    fs_packagesCollection(after: $after) {
        pageInfo { endCursor startCursor hasNextPage } 
        edges {
          node {
            nodeId
            id
            name
            code
            category
            fs_prodcategory { id catname }
            active 
            description 
            listorder 
          }
        }
      }
  }
`);

const deletePackageQuery = gql(`
    mutation deletePackagesQuery($filter:fs_packagesFilter) {
        deleteFromfs_packagesCollection(filter: $filter) {
            affectedCount
        }
    }
`);


interface EditToolbarProps {
    openModal: () => void;
}


function EditToolbar(props: EditToolbarProps) {
    const { openModal } = props;

    const handleClick = () => {
        openModal();
    };

    return (
        <GridToolbarContainer>
            <MDButtonRoot
                theme={setThemeObj}
                ownerState={{
                    size: "medium",
                    variant: "text",
                    color: "info",
                    textTransform: "uppercase",
                    circular: false,
                    iconOnly: false,
                    darkMode: false
                }}
                sx={{marginBottom:'8px'}}
                onClick={handleClick} startIcon={<AddIcon />}
            > Add package</MDButtonRoot>
        </GridToolbarContainer>
    );
}

// TODO: Handle all format conversion in the state managment library?
function formatPackages(test1: types.Fs_PackagesEdge[]): any[] {
    return test1.map(
        (item: types.Fs_PackagesEdge) => {
            const subsetItem = {
                nodeId: item.node.nodeId,
                id: item.node.id,
                name: item.node.name,
                code: item.node.code,
                productCategory: item.node.fs_prodcategory.catname,
                category: item.node.category,
                active: item.node.active,
                description: item.node.description,
                listorder: item.node.listorder,
            };
            return subsetItem;
        }
    ).sort((a, b) => {
        // First, compare by category in ascending order
        if (a.category < b.category) return -1;
        if (a.category > b.category) return 1;

        // If categories are the same, then compare by listorder in ascending order
        return a.listorder - b.listorder;
    });
}


function PackagesList() {

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'name', headerName: 'Package Name', width: 420 },
        { field: 'code', headerName: 'Code', width: 110 },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: (params) => {

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary icons-style"
                        onClick={handleEditClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary icons-style error"
                        onClick={handleDeleteClick(params.row)}
                        color="inherit"
                    />,
                ];
            }
        },
    ];

    const [editedRow, setEditedRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isSmallScreen = useScreenSize();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const handleAddClick = () => {
        setEditedRow(null); setIsModalOpen(true);
    };

    const handleEditClick = (row: any) => () => {
        setEditedRow(row);
        setIsModalOpen(true);
    };
    const handleSave = () => {
        // Handle saving the edited row data
        setIsModalOpen(false);
    };

    const { loading, error, data } = useQueryAll<types.Fs_PackagesEdge>({query: getPackagesQuery})

    if (loading) return (<div>Loading</div>)
    if (error) return (<div>Error</div>)

    const handleDeleteClick = (row: any) => () => {
        setEditedRow(row);
        setIsDeleteDialogOpen(true);
    };
    const classes = formatPackages(data);

    return (
        <Grid container spacing={1}>
            <Grid item xs={11}>
                <Card>
                    <MDBoxRoot
                        component="main" px={4} py={4}
                        sx={{ flexGrow: 1, width: '100%' }}
                    >
                        <Toolbar />
                        
                            <StyledBox theme={setThemeObj} sx={{marginTop:"30px"}}>
                                <MDButtonRoot
                                    theme={setThemeObj}
                                    ownerState={{
                                        size: "medium",
                                        variant: "text",
                                        color: "info",
                                        textTransform: "uppercase",
                                        circular: false,
                                        iconOnly: false,
                                        darkMode: false
                                    }}
                                    sx={{marginBottom:'8px'}}
                                    onClick={handleAddClick} startIcon={<AddIcon />}
                                > Add package</MDButtonRoot>
                                <DataGrid
                                    rows={classes}
                                    columns={columns}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { page: 0, pageSize: 100 },
                                        },
                                    }}
                                    pageSizeOptions={[100]}
                                />
                            </StyledBox>
                            {isModalOpen && (
                                <EditPackageDialog rowData={editedRow} onSave={handleSave} onClose={() => setIsModalOpen(false)} />
                            )}


                            {isDeleteDialogOpen && (<DeleteClassDialog rowData={editedRow} onClose={() => setIsDeleteDialogOpen(false)} />)}

                    </MDBoxRoot></Card></Grid></Grid>
    )
}
export default PackagesList;

interface DeleteClassDialogProps {
    rowData: any;
    onClose: () => void;
}
const DeleteClassDialog: FC<DeleteClassDialogProps> = ({ rowData, onClose }) => {

    //const { loading, data: cl_events_data } = useQuery(getEventsForClassQuery, { variables: { id: rowData.id }, fetchPolicy: 'network-only' });
    const [deletePackageMutation, { data: del_data, loading: del_loading, error: del_error }] = useMutation(deletePackageQuery, {
        refetchQueries: [
            'GetPackages' // Query name
        ],
    });
    //const linkedEventsCount = cl_events_data?.get_event_count_for_class;

const handleDeleteClass = async () => {
    const id = rowData.id;
    await deletePackageMutation({ variables: { filter: { id: { eq: id } } } })
    onClose();
};


return (
    <Dialog
        maxWidth="sm"
        open={true}
        fullWidth
    >
        <MDTypographyRoot variant="h5" px={1} py={1}>Delete package? <CloseIcon onClick={onClose} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypographyRoot>
        <DialogContent dividers>
            <MDTypographyRoot variant="h4" px={1} ownerState={{ color: "dark" }}>
                {'0' === '0' && `Deleting package "${rowData.name}"`}
            </MDTypographyRoot>
        </DialogContent>
        <DialogActions>
            {'0' === '0' && (
                <MDButtonRoot
                    ownerState={{
                        variant: "gradient",
                        color: "info",
                        textTransform: "none",
                        circular: false,
                        iconOnly: false,
                        darkMode: false
                    }}
                    size="medium"
                    onClick={handleDeleteClass}
                    type="submit">
                    Delete
                </MDButtonRoot>
            )}
            <MDButtonRoot
                size="medium"
                onClick={onClose}
                ownerState={{
                    textTransform: "none",
                    variant: "gradient",
                    color: "warning",
                }}>
                Cancel
            </MDButtonRoot>
        </DialogActions>
    </Dialog>);
}
